import { useCallback, useEffect, useState } from "react";
import { FAVORITES_EVENT, defaultFavoritesFor, favoritesKey, parseFavorites, toggleFavorite } from "./favoriteApps";

// Las de omisión dependen del rol (el mismo userRoleId que usa el menú de apps).
const roleDefaults = (): string[] => {
  try {
    return defaultFavoritesFor(localStorage.getItem("userRoleId"));
  } catch {
    return defaultFavoritesFor(null);
  }
};

const read = (key: string): string[] => {
  try {
    return parseFavorites(localStorage.getItem(key), roleDefaults());
  } catch {
    return parseFavorites(null, roleDefaults());
  }
};

// Favoritas por usuario y por navegador. Es una comodidad de quien usa la
// pantalla, no un dato de la cuenta: si localStorage no está (ventana privada,
// sitio bloqueado) se usan las de omisión y no se recuerda, sin romper nada.
// La estrella del menú y el riel están en componentes distintos: el evento
// FAVORITES_EVENT los sincroniza al instante en la misma pestaña, y "storage"
// entre pestañas.
export function useFavoriteApps(): {
  favorites: string[];
  isFavorite: (id: string) => boolean;
  toggle: (id: string) => void;
} {
  let userId: string | null = null;
  try {
    userId = localStorage.getItem("userId");
  } catch {
    userId = null;
  }
  const key = favoritesKey(userId);
  const [favorites, setFavorites] = useState<string[]>(() => read(key));

  useEffect(() => {
    setFavorites(read(key));
    const sync = () => setFavorites(read(key));
    const onStorage = (e: StorageEvent) => e.key === key && sync();
    window.addEventListener(FAVORITES_EVENT, sync);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(FAVORITES_EVENT, sync);
      window.removeEventListener("storage", onStorage);
    };
  }, [key]);

  const toggle = useCallback(
    (id: string) => {
      const next = toggleFavorite(read(key), id);
      setFavorites(next);
      try {
        localStorage.setItem(key, JSON.stringify(next));
      } catch {
        /* sin almacenamiento: vale sólo para esta vista */
      }
      window.dispatchEvent(new Event(FAVORITES_EVENT));
    },
    [key],
  );

  const isFavorite = useCallback((id: string) => favorites.includes(id), [favorites]);
  return { favorites, isFavorite, toggle };
}
